import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, BriefcaseBusiness, Users, Workflow, Zap, ScanSearch, Bot } from "lucide-react";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import EducationPhotoSlider from "@/components/EducationPhotoSlider";
import Footer from "@/components/Footer";
import BrandArrow from "@/components/BrandArrow";
import EducationContactSection from "@/components/EducationContactSection";
import EducationFAQSection from "@/components/EducationFAQSection";
import { educationPrograms, educationSkills, educationAudiences } from "@/data/education";
import "@/styles/education.css";

const skillIcons = [Zap, ScanSearch, Bot];
const audienceIcons = [BriefcaseBusiness, Users, Workflow];
const reasons = [
  { title: "ИИ становится частью ежедневной работы", text: "Команде важно понимать возможности технологий и применять их в своей профессии." },
  { title: "Практика помогает освоить инструменты", text: "Работа со своими задачами закрепляет навыки и помогает перенести их в процессы компании." },
  { title: "ИИ-инициативы рождаются внутри команды", text: "Сотрудники знают свои процессы и могут находить сценарии, которые принесут бизнесу результат." },
];
const expertise = [
  { title: "Практический опыт", text: "Занимаемся AI-трансформацией, создаём ИИ-решения и работаем с реальными процессами бизнеса." },
  { title: "Обучение на задачах бизнеса", text: "Адаптируем программы под функции, процессы и уровень участников. Практику строим вокруг ежедневных задач." },
  { title: "От навыка к инициативе", text: "Помогаем увидеть процессы, где ИИ даст эффект, сформулировать сценарий и определить следующий шаг." },
  { title: "Технологическая глубина", text: "Работаем с большими языковыми моделями, корпоративными ИИ-системами, агентами, автоматизацией и собственными продуктами." },
];

export default function Education() {
  const [program, setProgram] = useState("");

  useEffect(() => {
    const oldTitle = document.title;
    document.title = "Обучение ИИ для бизнеса: корпоративные программы и AI-тренинги | WMT AI";
    const updates = [
      { selector: 'meta[name="description"]', tag: "meta", key: "content", value: "Корпоративное обучение ИИ для руководителей и команд: практические AI-сценарии, стратегические сессии, обучение работе с нейросетями и ИИ-агентами | Программы WMT AI для бизнеса", attributes: { name: "description" } },
      { selector: 'link[rel="canonical"]', tag: "link", key: "href", value: "https://wmt-ai.ru/education", attributes: { rel: "canonical" } },
      { selector: 'meta[property="og:title"]', tag: "meta", key: "content", value: "Обучение ИИ для бизнеса: корпоративные программы и AI-тренинги | WMT AI", attributes: { property: "og:title" } },
      { selector: 'meta[property="og:url"]', tag: "meta", key: "content", value: "https://wmt-ai.ru/education", attributes: { property: "og:url" } },
    ];
    const cleanups = updates.map(update => {
      const existing = document.head.querySelector(update.selector);
      const element = existing ?? document.createElement(update.tag);
      const previous = element.getAttribute(update.key);
      Object.entries(update.attributes).forEach(([key, value]) => element.setAttribute(key, value));
      element.setAttribute(update.key, update.value);
      if (!existing) document.head.append(element);
      return () => { if (!existing) element.remove(); else if (previous === null) element.removeAttribute(update.key); else element.setAttribute(update.key, previous); };
    });
    return () => { document.title = oldTitle; cleanups.forEach(cleanup => cleanup()); };
  }, []);

  const chooseProgram = (id: string) => {
    setProgram(id);
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return <div className="education-page min-h-screen bg-background text-foreground">
    <Navbar />
    <main id="page-shell">
      <HeroSection
        title="Обучение ИИ для бизнеса"
        description="Обучение искусственному интеллекту для бизнеса помогает руководителям и сотрудникам применять ИИ и нейросети в рабочих процессах. На программах WMT AI участники находят возможности автоматизации и создают инструменты для своих задач."
        ctaLabel="Подобрать программу"
        ctaTarget="programs"
      />

      <section id="skills" className="education-section education-dark education-section--rounded">
        <div className="education-container">
          <div className="education-section-head">
            <h2 className="education-heading">Учим использовать ИИ <span className="text-accent">в реальной работе</span></h2>
            <p className="education-intro">Практические курсы по искусственному интеллекту и нейросетям помогают освоить инструменты на рабочих задачах. Участники учатся проверять ответы моделей, учитывать ограничения ИИ и применять его в ежедневной работе.</p>
          </div>
          <div className="education-three-grid mt-10">
            {educationSkills.map((skill, index) => {
              const Icon = skillIcons[index];
              return <article key={skill.title} className="education-skill">
                <div className="education-card-heading"><Icon size={26} strokeWidth={1.5} className="text-accent" aria-hidden="true" /><h3>{skill.title}</h3></div>
                <p>{skill.text}</p>
              </article>;
            })}
          </div>
          <p className="education-note">Обучение строится вокруг задач участников, практики и реальных бизнес-сценариев.</p>
        </div>
      </section>

      <section className="education-section education-why education-section--rounded">
        <div className="education-container">
          <div className="education-section-head">
            <h2 className="education-heading">Компаниям нужны люди, <span className="text-accent">которые умеют работать с ИИ</span></h2>
            <p className="education-intro">Практическое обучение сотрудников помогает применять нейросети к собственным задачам, находить возможности автоматизации и сокращать рутинную работу. Команда формирует внутреннюю экспертизу по использованию ИИ в процессах компании.</p>
          </div>
          <div className="education-practice-grid">
            <figure className="education-photo">
              <img src="/hero/slides/hero-personal-ai.webp" alt="Практическое обучение на программе «Личный ИИ» WMT AI" loading="lazy" />
            </figure>
            <div className="education-reasons">
              {reasons.map((reason, index) => <article key={reason.title}>
                <h3><span className="education-index">0{index + 1}</span><span>{reason.title}</span></h3>
                <p>{reason.text}</p>
              </article>)}
            </div>
          </div>
          <div className="education-statement">
            <BrandArrow className="text-accent shrink-0 w-6 h-6" />
            <p>Мы учим команду самостоятельно видеть возможности ИИ в своей работе и переводить их в конкретные действия.</p>
            <a href="#programs" className="wmt-cta wmt-cta--dark"><span className="optical-center">Подобрать обучение</span></a>
          </div>
        </div>
      </section>

      <section id="audiences" className="education-section education-section--surface education-section--rounded">
        <div className="education-container">
          <h2 className="education-heading max-w-[820px]">Программы для <span className="text-accent">разных ролей в компании</span></h2>
          <div className="education-three-grid mt-10">
            {educationAudiences.map((audience, index) => {
              const Icon = audienceIcons[index];
              return <article key={audience.title} className="education-audience">
                <div className="education-card-heading"><Icon size={26} strokeWidth={1.5} className="text-accent" aria-hidden="true" /><h3>{audience.title}</h3></div>
                <p>{audience.text}</p>
                <div className="education-result"><span>Результат</span><p>{audience.result}</p></div>
              </article>;
            })}
          </div>
        </div>
      </section>

      <section id="programs" className="education-section education-dark education-section--rounded">
        <div className="education-container">
          <h2 className="education-heading max-w-[780px]">Выберите <span className="text-accent">подходящий формат</span></h2>
          <div className="education-program-grid">
            {educationPrograms.map((item, index) => {
              const content = <>
                <span className="education-program-number">0{index + 1}</span>
                <h3>{item.title}</h3>
                <p className="education-program-lead">{item.lead}</p>
                <p>{item.description}</p>
                {"topics" in item && <p>{item.topics}</p>}
                <dl>
                  <div><dt>Для кого</dt><dd>{item.audience}</dd></div>
                  <div><dt>Формат</dt><dd>{item.format}</dd></div>
                  <div><dt>Результат</dt><dd>{item.result}</dd></div>
                </dl>
              </>;

              if (item.id === "personal") {
                return <Link id="program-personal" key={item.id} to="/education/personal-ai" className="education-program education-program-link" aria-label="Подробнее о программе Личный ИИ">
                  {content}
                  <span className="education-program-cta"><span>{item.cta}</span><BrandArrow direction="up-right" className="w-4 h-4" /></span>
                </Link>;
              }

              return <article id={`program-${item.id}`} key={item.id} className="education-program">
                {content}
                <button type="button" className="education-program-cta" onClick={() => chooseProgram(item.id)}><span>{item.cta}</span><BrandArrow direction="up-right" className="w-4 h-4" /></button>
              </article>;
            })}
          </div>
          <div id="courses" className="education-courses">
            <div className="education-program-number">05</div>
            <div><h3>Готовые онлайн-курсы</h3><p className="text-white mt-2">Учитесь в собственном темпе</p><p>Готовые программы для самостоятельного изучения отдельных инструментов и навыков.</p></div>
            <Link to="/education/courses" className="education-text-link">Перейти к каталогу<BrandArrow direction="up-right" className="h-3 w-3" /></Link>
          </div>
        </div>
      </section>

      <section id="education-expertise" className="education-section education-section--surface education-section--rounded">
        <div className="education-container">
          <h2 className="education-heading max-w-[950px]">Обучаем тому, что сами применяем в <span className="text-accent">AI-трансформации</span></h2>
          <div className="education-expertise-layout">
            <EducationPhotoSlider />
            <div className="education-expertise-grid">
            {expertise.map(item => <article key={item.title} className={item.title === "Обучение на задачах бизнеса" ? "education-expertise-accent" : undefined}><h3>{item.title}</h3><p>{item.text}</p></article>)}
            </div>
          </div>
        </div>
      </section>

      <section className="education-section education-dark education-section--rounded education-transformation-path">
        <div className="education-container">
          <h2 className="education-heading max-w-[950px]">Обучение может стать первым шагом <span className="text-accent">AI-трансформации</span></h2>
          <p className="education-intro max-w-[750px]">Обучение ИИ может стать первым этапом системного внедрения искусственного интеллекта. WMT AI помогает пройти путь от обучения сотрудников и поиска сценариев до запуска пилотных проектов, автоматизации процессов и масштабирования успешных решений.</p>
          <ol className="education-path">{["Обучение", "Инициативы", "Пилоты", "Масштабирование"].map((step, index) => <li key={step}><span className="education-index">0{index + 1}</span><span>{step}</span>{index < 3 && <ArrowRight size={22} aria-hidden="true" />}</li>)}</ol>
          <a href="/" className="wmt-cta wmt-cta--accent"><span className="optical-center">Как проходит AI-трансформация</span><ArrowUpRight size={18} className="shrink-0" /></a>
        </div>
      </section>

      <EducationContactSection program={program} onProgramChange={setProgram} />
      <EducationFAQSection />
    </main>
    <Footer />
  </div>;
}
